import { useMemo } from 'react';
import { nullish } from '../../../shared/types/nullish.ts';
import { SpcResultsPos } from '../../../shared/types/spc.ts';
import { SPC_DESCRIPTIONS } from '../../../shared/utils/spcConstants.ts';
import { MGraphMeasurement } from '../../components/measurement-graph/measurementsGraph.tsx';
import { getUsedTool } from '../../services/getUsedTool.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


interface Props {
    spcResults: SpcResultsPos[] | nullish;
    activePositionIdx: number | nullish;
    spcTogglesMap: Record<string, boolean[]> | nullish;
}


export function useMGraphAdapter({
    spcResults,
    activePositionIdx,
    spcTogglesMap,
}: Props) {

    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery(
        { type: 'cmm/vmm' as const },
        { refetchOnWindowFocus: false }
    );


    const measurements = useMemo(() => {
        if (!spcResults || !spcTogglesMap || activePositionIdx == null) return null;

        const spcResult = spcResults[activePositionIdx];
        const position = spcResult.position;
        const toggles = spcTogglesMap[position.positionNumber];

        const measurements: MGraphMeasurement[] = [];
        for (const input of spcResult.measurements) {
            const isHighlighted = input.spcResult.some((flag, idx) => flag && toggles[idx]);
            const source = getUsedTool(input.recordPosition, position, cmmEquipments);
            const issues = input.spcResult
                .map((it, i) => it ? SPC_DESCRIPTIONS[i] : null)
                .filter(it => it != null);

            const measurement = {
                value: input.recordPosition.value as string | null,
                isHighlighted,
                date: input.record.measurementStart!,
                partCount: input.record.partCount ?? null,
                partIndex: input.partIndex,
                hasMultipleParts: input.hasMultipleParts,
                source,
                issues,
            };
            measurements.push(measurement);
        }
        return measurements;
    }, [spcResults, spcTogglesMap, activePositionIdx, cmmEquipments]);

    return {
        measurements,
    };
}
